<template>
  <div class="precision-picker" ref="pickerRoot">
    <!-- Decimal Precision -->
    <div class="precision-picker__section">
      <div :id="labelId" class="precision-picker__field-label">Decimal Precision</div>
      <div
        class="precision-picker__trigger precision-picker__trigger--format"
        :class="{
          'precision-picker__trigger--open': decimalOpen,
          'precision-picker__trigger--disabled': disabled,
        }"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="decimalOpen ? 'true' : 'false'"
        :aria-disabled="disabled ? 'true' : 'false'"
        :aria-labelledby="labelId"
        @click="toggleDecimalDropdown"
        :tabindex="disabled ? -1 : 0"
        @keydown.enter="toggleDecimalDropdown"
        @keydown.space.prevent="toggleDecimalDropdown"
        @keydown.escape="closeDecimalDropdown"
      >
        <div class="precision-picker__content">
          <div class="precision-picker__input-content">
            <img src="@components/assets/decimal-precision.svg" alt="decimal" class="precision-picker__decimal-icon" />
            <span class="precision-picker__value">{{ decimalPrecision }} </span>
          </div>
        </div>
        <v-icon
          class="precision-picker__chevron"
          :class="{ 'precision-picker__chevron--rotated': decimalOpen }"
        >
          mdi-chevron-down
        </v-icon>
      </div>
      <div v-if="decimalOpen" class="precision-picker__dropdown">
        <div class="precision-picker__menu" role="listbox" :aria-labelledby="labelId">
          <div
            v-for="opt in decimalOptions"
            :key="opt"
            role="option"
            :aria-selected="decimalPrecision === opt ? 'true' : 'false'"
            class="precision-picker__menu-item"
            :class="{
              'precision-picker__menu-item--selected':
                decimalPrecision === opt,
            }"
            @click="selectDecimal(opt)"
            tabindex="0"
            @keydown.enter="selectDecimal(opt)"
            @keydown.space.prevent="selectDecimal(opt)"
          >
            <span class="precision-picker__menu-label">{{ opt }}</span>
            <img
              v-if="decimalPrecision === opt"
              src="@components/assets/rightIcon.svg"
              alt=""
              aria-hidden="true"
              class="precision-picker__check-icon"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: undefined,
  },
  decimalPrecisionModel: {
    type: [Number, String],
    default: 0,
  },
  placeholder: {
    type: String,
    default: "Place Holder", // Floating label shown when dropdown is open
  },
  decimalOptions: {
    type: Array,
    default: () => [0, 1, 2, 3, 4, 5, 6],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:modelValue",
  "update:decimalPrecisionModel",
  "change:decimal",
]);

const pickerRoot = ref(null);
const decimalOpen = ref(false);
const labelId = `precision-picker-label-${getCurrentInstance().proxy._uid}`;

// Default to 0 if value is undefined, null, empty string, or NaN
function normalize(val) {
  return Number(val) || 0;
}

const externalValue = computed(() =>
  props.modelValue !== undefined ? props.modelValue : props.decimalPrecisionModel
);

const decimalPrecision = ref(normalize(externalValue.value));

watch(externalValue, (val) => {
  decimalPrecision.value = normalize(val);
});

function toggleDecimalDropdown() {
  if (props.disabled) return;
  decimalOpen.value = !decimalOpen.value;
}

function closeDecimalDropdown() {
  decimalOpen.value = false;
}

function selectDecimal(value) {
  decimalPrecision.value = value;
  emit("update:modelValue", value);
  emit("update:decimalPrecisionModel", value);
  emit("change:decimal", value);
  closeDecimalDropdown();
}

function handleClickOutside(event) {
  if (pickerRoot.value && !pickerRoot.value.contains(event.target)) {
    closeDecimalDropdown();
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.precision-picker {
  font-family: Inter, sans-serif;
}

.precision-picker__section {
  position: relative;
  width: 184px;
}

.precision-picker__field-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--colors-text-text-secondary-700, #344054);
  margin-bottom: 6px;
}

.precision-picker__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background-color: var(--Colors-Background-bg-primary, #ffffff);
  border: 1px solid var(--Colors-Border-border-primary, #d0d5dd);
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.precision-picker__trigger--format {
  position: relative;
}

.precision-picker__trigger:focus,
.precision-picker__trigger--open {
  border-color: var(--colors-border-border-brand, #d6bbfb);
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05),
    0px 0px 0px 4px rgba(158, 119, 237, 0.24);
}

.precision-picker__trigger--disabled,
.precision-picker__trigger--disabled:focus {
  background-color: #f9fafb;
  border-color: var(--Colors-Border-border-primary, #d0d5dd);
  box-shadow: none;
  cursor: not-allowed;
}

.precision-picker__content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.precision-picker__input-content {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  width: 100%;
  overflow: visible;
  position: relative;
}

.precision-picker__decimal-icon {
  width: 22px;
  height: 22px;
  aspect-ratio: 1/1;
}

.precision-picker__value {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--colors-text-text-primary-900, #101828);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.precision-picker__chevron {
  flex-shrink: 0;
  color: var(--colors-text-text-disabled, #667085) !important;
  font-size: 20px !important;
  transition: transform 0.2s ease;
}

.precision-picker__chevron--rotated {
  transform: rotate(180deg);
}

.precision-picker__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: var(--Colors-Background-bg-primary, #ffffff);
  border: 1px solid var(--Colors-Border-border-secondary, #eaecf0);
  border-radius: 8px;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

.precision-picker__menu {
  padding: 4px 0;
}

.precision-picker__menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 10px 8px;
  margin: 1px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  outline: none;
}

.precision-picker__menu-item:hover {
  background-color: var(--component-colors-utility-gray-utility-gray-50, #f9fafb);
}

.precision-picker__menu-item:focus {
  background-color: var(--component-colors-utility-gray-utility-gray-50, #f9fafb);
  box-shadow: inset 0 0 0 2px var(--primary-normal, #7f56d9);
}

.precision-picker__menu-item--selected {
  background-color: var(--component-colors-utility-gray-utility-gray-50, #f9fafb);
}

.precision-picker__menu-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--colors-text-text-primary-900, #101828);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.precision-picker__check-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}
</style>
